// react
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// mui
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import MuiDialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
// components
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import AdminTablePage from '@karehero/llama/pwa/templates/AdminTablePage/AdminTablePage';
import DataTable, {
  GridColDef,
  GridRenderCellParams,
} from '@karehero/llama/pwa/organisms/DataTable/DataTable';

// store
import { useGetAllReferringPartnersWithOrganizationsQuery } from 'store/api/referringPartner';
import {
  useGetAllOrganizationQuery,
  useLazyGetOrganizationQuery,
  useUpdateOrganizationMutation,
} from 'store/api/organization';
import type { Organization } from '@karehero/models';
import { newToast } from 'store/slices/uiSlice';
import { useDispatch } from 'react-redux';

// routes
import { DYNAMIC_ROUTES, ROUTES } from 'router/routes';

interface ReferringPartnerTableRow {
  id: string;
  name: string;
  domains: string[];
  orgCount: number;
  status: 'Active' | 'Onboarding';
  organizationIds: string[];
  isOrgRow?: boolean;
  orgId?: string;
  orgName?: string;
  partnerId?: string;
}

/**
 * ReferringPartnerTable lists referring partners with org count, status, and expandable org rows.
 */
export const ReferringPartnerTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [linkDialog, setLinkDialog] = useState<{
    open: boolean;
    partnerId: string;
    partnerName: string;
  }>({ open: false, partnerId: '', partnerName: '' });
  const [linkSelectedOrg, setLinkSelectedOrg] = useState<Organization | null>(
    null,
  );

  const { data: list } = useGetAllReferringPartnersWithOrganizationsQuery();
  // TODO[pagination]: replace with searchable autocomplete
  const { data: allOrgsData } = useGetAllOrganizationQuery({ perPage: 1000 });
  const allOrgs = allOrgsData?.data;
  const [getOrganization] = useLazyGetOrganizationQuery();
  const [updateOrganization] = useUpdateOrganizationMutation();

  const orgNameById = useMemo(() => {
    const map = new Map<string, string>();
    list?.forEach((item) => {
      (item.organizations ?? []).forEach((o: { id: string; name: string }) =>
        map.set(o.id, o.name),
      );
    });
    return map;
  }, [list]);

  const linkedOrgIdsByPartnerId = useMemo(() => {
    const map = new Map<string, Set<string>>();
    list?.forEach((item) => {
      map.set(item.partner.id, new Set(item.organizationIds ?? []));
    });
    return map;
  }, [list]);

  const openLinkDialog = useCallback((row: ReferringPartnerTableRow) => {
    if (row.isOrgRow) return;
    setLinkDialog({
      open: true,
      partnerId: row.id,
      partnerName: row.name,
    });
    setLinkSelectedOrg(null);
  }, []);

  const closeLinkDialog = useCallback(() => {
    setLinkDialog((prev) => ({ ...prev, open: false }));
    setLinkSelectedOrg(null);
  }, []);

  const orgsAvailableToLink = useMemo(() => {
    if (!allOrgs || !linkDialog.open || !linkDialog.partnerId) return [];
    return allOrgs.filter((org) => !org.partnerId);
  }, [allOrgs, linkDialog.open, linkDialog.partnerId, linkedOrgIdsByPartnerId]);

  const handleLinkOrganization = useCallback(async () => {
    if (!linkSelectedOrg || !linkDialog.partnerId) return;
    try {
      await updateOrganization({
        ...linkSelectedOrg,
        partnerId: linkDialog.partnerId,
      }).unwrap();
      dispatch(
        newToast({
          title: 'Organisation linked',
          description:
            'The organisation is now linked to this referring partner.',
          variant: 'success',
        }),
      );
      closeLinkDialog();
    } catch {
      dispatch(
        newToast({
          title: 'Error',
          description: 'Failed to link organisation.',
          variant: 'error',
        }),
      );
    }
  }, [
    linkSelectedOrg,
    linkDialog.partnerId,
    updateOrganization,
    dispatch,
    closeLinkDialog,
  ]);

  const unlinkOrg = useCallback(
    async (orgId: string) => {
      try {
        const org = await getOrganization(orgId).unwrap();
        await updateOrganization({ ...org, partnerId: null }).unwrap();
        dispatch(
          newToast({
            title: 'Unlinked',
            description:
              'Organisation is no longer linked to this referring partner.',
            variant: 'success',
          }),
        );
      } catch {
        dispatch(
          newToast({
            title: 'Error',
            description: 'Failed to unlink organisation.',
            variant: 'error',
          }),
        );
      }
    },
    [getOrganization, updateOrganization, dispatch],
  );

  const toggleExpanded = useCallback((rowId: string) => {
    setExpandedRowId((prev) => (prev === rowId ? null : rowId));
  }, []);

  const rows: ReferringPartnerTableRow[] = useMemo(() => {
    if (!list) return [];
    const out: ReferringPartnerTableRow[] = [];
    for (const item of list) {
      const organizationIds = item.organizationIds ?? [];
      const orgCount = organizationIds.length;
      const partnerRow: ReferringPartnerTableRow = {
        id: item.partner.id,
        name: item.partner.name,
        domains: item.partner.domains ?? [],
        orgCount,
        status: orgCount > 0 ? 'Active' : 'Onboarding',
        organizationIds,
      };
      out.push(partnerRow);
      if (expandedRowId === item.partner.id) {
        organizationIds.forEach((orgId) => {
          out.push({
            id: `org-${orgId}`,
            name: '',
            domains: [],
            orgCount: 0,
            status: 'Onboarding',
            organizationIds: [],
            isOrgRow: true,
            orgId,
            orgName: orgNameById.get(orgId) ?? orgId,
            partnerId: item.partner.id,
          });
        });
      }
    }
    return out;
  }, [list, expandedRowId, orgNameById]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Partner Name',
        flex: 1,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as ReferringPartnerTableRow;
          if (row.isOrgRow && row.orgId != null && row.orgName != null) {
            return (
              <span style={{ paddingLeft: 24, display: 'inline-block' }}>
                <Link
                  to={`/admin/organization/${row.orgId}/edit`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {row.orgName}
                </Link>
              </span>
            );
          }
          const isExpandable = row.orgCount >= 1;
          const isExpanded = expandedRowId === row.id;
          return (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {isExpandable ? (
                <button
                  type='button'
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded(row.id);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  <Icon
                    icon={isExpanded ? 'chevron-down' : 'chevron-right'}
                    size='sm'
                  />
                </button>
              ) : (
                <span style={{ width: 16 }} />
              )}
              {row.name}
            </span>
          );
        },
      },
      {
        field: 'domains',
        headerName: 'Email Domains',
        flex: 0.8,
        minWidth: 150,
        valueGetter: (_value: any, row: any) => {
          if (row.isOrgRow) return '';
          const d = row.domains;
          return Array.isArray(d) ? d.join(', ') : '';
        },
      },
      {
        field: 'orgCount',
        headerName: 'Organisation Count',
        flex: 0.5,
        minWidth: 130,
        valueGetter: (_value: any, row: any) => {
          if (row.isOrgRow) return '';
          return row.orgCount;
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.4,
        minWidth: 100,
        valueGetter: (_value: any, row: any) => {
          if (row.isOrgRow) return '';
          return row.status;
        },
      },
    ],
    [expandedRowId, toggleExpanded],
  );

  return (
    <AdminTablePage title='Referring Partner Management'>
      <DataTable
        columns={columns}
        rows={rows}
        actions={[
          {
            label: 'Configure',
            icon: 'gear',
            onPress: (row: ReferringPartnerTableRow) => {
              if (row.isOrgRow) return;
              navigate(DYNAMIC_ROUTES.ADMIN_REFERRING_PARTNER_EDIT(row.id));
            },
            isHidden: (row: ReferringPartnerTableRow) => !!row.isOrgRow,
          },
          {
            label: 'Link organisation',
            icon: 'link',
            onPress: openLinkDialog,
            isHidden: (row: ReferringPartnerTableRow) => !!row.isOrgRow,
          },
          {
            label: 'Unlink',
            icon: 'link-slash',
            onPress: (row: ReferringPartnerTableRow) => {
              if (row.isOrgRow && row.orgId) unlinkOrg(row.orgId);
            },
            isHidden: (row: ReferringPartnerTableRow) => !row.isOrgRow,
          },
        ]}
        toolbarActions={
          <MuiButton
            variant='contained'
            size='small'
            onClick={() => navigate(ROUTES.ADMIN_REFERRING_PARTNER_NEW)}
            startIcon={<Icon icon='plus' size='sm' />}
          >
            Add Referring Partner
          </MuiButton>
        }
      />
      <MuiDialog
        open={linkDialog.open}
        onClose={closeLinkDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Link organisation to {linkDialog.partnerName}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Autocomplete
              options={orgsAvailableToLink}
              getOptionLabel={(org) => org.name}
              value={linkSelectedOrg}
              onChange={(_, value) => setLinkSelectedOrg(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Organisation'
                  size='small'
                  placeholder='Type to search…'
                />
              )}
              sx={{ mt: 1, minWidth: 240 }}
            />
            {orgsAvailableToLink.length === 0 &&
              allOrgs &&
              allOrgs.length > 0 && (
                <Typography variant='body2'>
                  All organisations are already linked to this partner.
                </Typography>
              )}
          </Box>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={closeLinkDialog}>Cancel</MuiButton>
          <MuiButton
            variant='contained'
            onClick={handleLinkOrganization}
            disabled={!linkSelectedOrg}
          >
            Link
          </MuiButton>
        </DialogActions>
      </MuiDialog>
    </AdminTablePage>
  );
};

export default ReferringPartnerTable;
